import { useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import {
  commissionsApi,
  type AdminClaimListQuery,
  type ClaimStatus,
  type CommissionClaimSummaryDto,
  type RevenuePoolDto,
  type RevisionState,
} from "@/api/commissions";
import { Button, Card, FormField, Input } from "@/design-system/components/ui";
import { ALL_CLAIM_STATUSES, ClaimStatusBadge, RevisionStateBadge } from "@/components/commissions/ClaimStatusBadge";
import { ClaimLoan } from "@/components/commissions/ClaimLoan";
import { ClaimClaimant } from "@/components/commissions/ClaimHeld";
import { currency, percent, shortDate, truncate } from "@/components/commissions/formatters";
import { useDateFormatter } from "@/utils/datetime";

// DERIVED, never retyped. This list used to be a hand-written ten of eleven —
// it omitted `Suggested`, so the one page where an admin reviews the whole book
// could not filter to the claims that need action, on a status that is the
// entry state for every auto-suggested claim. Nothing caught it: a subset of a
// union is a valid `ClaimStatus[]`, and the server accepted the value all
// along. See ALL_CLAIM_STATUSES for why deriving makes a subset impossible.
const STATUS_OPTIONS = ALL_CLAIM_STATUSES;

const REVISION_OPTIONS: Array<{ value: RevisionState | ""; label: string }> = [
  { value: "", label: "Any" },
  { value: "None", label: "None" },
  { value: "RevisionInProgress", label: "Revising" },
];

const PAGE_SIZE = 50;

export function AdminAllClaimsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the source of truth so a filtered view is bookmarkable.
  const query = useMemo<AdminClaimListQuery>(() => ({
    page: parseInt(searchParams.get("page") ?? "1", 10) || 1,
    pageSize: PAGE_SIZE,
    statuses: searchParams.get("statuses") || undefined,
    poolIds: searchParams.get("poolIds") || undefined,
    claimantId: searchParams.get("claimantId") || undefined,
    loanRef: searchParams.get("loanRef") || undefined,
    dateFrom: searchParams.get("dateFrom") || undefined,
    dateTo: searchParams.get("dateTo") || undefined,
    minAmount: parseDecimalParam(searchParams.get("minAmount")),
    maxAmount: parseDecimalParam(searchParams.get("maxAmount")),
    revisionState: parseRevisionState(searchParams.get("revisionState")),
    q: searchParams.get("q") || undefined,
    sortBy: searchParams.get("sortBy") || undefined,
    sortDirection: parseSortDirection(searchParams.get("sortDirection")),
  }), [searchParams]);

  const claims = useQuery({
    queryKey: ["admin", "commissions", "claims", query],
    queryFn: () => commissionsApi.adminListClaims(query),
  });

  const pools = useQuery({
    queryKey: ["commissions", "pools", "all"],
    queryFn: () => commissionsApi.listPools(true),
  });

  // Every change is ONE setSearchParams call. react-router hands a functional
  // update the params of the render the setter came from, not the result of an
  // earlier call in the same handler, so two calls in a row keep only the
  // second. That is how a header click lost its column: `sortBy` was set, then
  // the `sortDirection` call started again from the old params and dropped it.
  const setParams = (updates: Record<string, string | undefined>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === undefined || value === "") next.delete(key);
        else next.set(key, value);
      }
      // Reset to page 1 whenever a filter or the sort changes.
      if (!("page" in updates)) next.delete("page");
      return next;
    });
  };
  const setParam = (key: string, value: string | undefined) => setParams({ [key]: value });

  const toggleStatus = (status: ClaimStatus) => {
    const current = (query.statuses ?? "").split(",").filter(Boolean);
    const next = current.includes(status)
      ? current.filter(s => s !== status)
      : [...current, status];
    setParam("statuses", next.length > 0 ? next.join(",") : undefined);
  };

  const togglePool = (poolId: string) => {
    const current = (query.poolIds ?? "").split(",").filter(Boolean);
    const next = current.includes(poolId)
      ? current.filter(p => p !== poolId)
      : [...current, poolId];
    setParam("poolIds", next.length > 0 ? next.join(",") : undefined);
  };

  // The text, date and amount inputs are uncontrolled (they commit on blur or
  // Enter), so clearing the URL did not clear what they showed, and the next
  // blur put the old value straight back. They are emptied here, in the click
  // handler. Remounting them under a new key does not work: react-router
  // applies the URL change as a transition, so the remount renders first, with
  // the old URL's values as its defaults.
  const filterInputsRef = useRef<HTMLDivElement>(null);
  const clearFilters = () => {
    filterInputsRef.current?.querySelectorAll("input").forEach((input) => {
      input.value = "";
    });
    setSearchParams(new URLSearchParams());
  };

  const selectedStatuses = (query.statuses ?? "").split(",").filter(Boolean) as ClaimStatus[];
  const selectedPoolIds = (query.poolIds ?? "").split(",").filter(Boolean);

  const totalPages = Math.max(1, Math.ceil((claims.data?.totalCount ?? 0) / PAGE_SIZE));
  const currentPage = query.page ?? 1;

  return (
    <div className="flex flex-col gap-space-5">
      <div className="flex items-end justify-between gap-space-3">
        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
            Commissions · Admin
          </div>
          <h1 className="font-display font-extrabold text-h2 uppercase tracking-wider text-onyx m-0">
            All claims
          </h1>
          <p className="text-body text-gray-mid mt-space-2 max-w-reading">
            Browse every commission claim across the firm. Filters are saved in the URL so
            you can share a view by sending the link.
          </p>
        </div>
        <a
          href={commissionsApi.adminClaimsExportUrl(query)}
          download
          className="inline-flex"
        >
          <Button variant="secondary">
            <Download size={14} strokeWidth={2} aria-hidden /> Export CSV
          </Button>
        </a>
      </div>

      <Card className="flex flex-col gap-space-3">
        <div ref={filterInputsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-space-3">
          <FormField label="Search" htmlFor="q">
            <Input
              id="q"
              defaultValue={query.q ?? ""}
              placeholder="Claim #, description, loan ref…"
              onBlur={(e) => setParam("q", e.target.value || undefined)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setParam("q", (e.target as HTMLInputElement).value || undefined);
              }}
            />
          </FormField>
          <FormField label="Loan" htmlFor="loanRef">
            <Input
              id="loanRef"
              defaultValue={query.loanRef ?? ""}
              placeholder="Loan number, id, or reference"
              onBlur={(e) => setParam("loanRef", e.target.value || undefined)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setParam("loanRef", (e.target as HTMLInputElement).value || undefined);
              }}
            />
          </FormField>
          <FormField label="Claimant user id" htmlFor="claimantId">
            <Input
              id="claimantId"
              defaultValue={query.claimantId ?? ""}
              placeholder="User id"
              onBlur={(e) => setParam("claimantId", e.target.value || undefined)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setParam("claimantId", (e.target as HTMLInputElement).value || undefined);
              }}
            />
          </FormField>
          <FormField label="Revision state" htmlFor="revisionState">
            <select
              id="revisionState"
              className="h-10 px-space-3 border border-border-light rounded-md bg-white-pure text-small"
              value={query.revisionState ?? ""}
              onChange={(e) => setParam("revisionState", e.target.value || undefined)}
            >
              {REVISION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Date from (created)" htmlFor="dateFrom">
            <Input
              id="dateFrom"
              type="date"
              defaultValue={query.dateFrom?.slice(0, 10) ?? ""}
              onBlur={(e) => setParam("dateFrom", e.target.value || undefined)}
            />
          </FormField>
          <FormField label="Date to (created)" htmlFor="dateTo">
            <Input
              id="dateTo"
              type="date"
              defaultValue={query.dateTo?.slice(0, 10) ?? ""}
              onBlur={(e) => setParam("dateTo", e.target.value || undefined)}
            />
          </FormField>
          <FormField label="Min amount" htmlFor="minAmount">
            <Input
              id="minAmount"
              type="number"
              step="0.01"
              defaultValue={query.minAmount ?? ""}
              onBlur={(e) => setParam("minAmount", e.target.value || undefined)}
            />
          </FormField>
          <FormField label="Max amount" htmlFor="maxAmount">
            <Input
              id="maxAmount"
              type="number"
              step="0.01"
              defaultValue={query.maxAmount ?? ""}
              onBlur={(e) => setParam("maxAmount", e.target.value || undefined)}
            />
          </FormField>
        </div>

        <div className="flex flex-col gap-space-2">
          <div className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
            Status
          </div>
          <div className="flex flex-wrap gap-space-2">
            {STATUS_OPTIONS.map((s) => (
              <FilterChip
                key={s}
                label={s}
                active={selectedStatuses.includes(s)}
                onClick={() => toggleStatus(s)}
              />
            ))}
          </div>
        </div>

        {pools.data && pools.data.length > 0 && (
          <div className="flex flex-col gap-space-2">
            <div className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
              Pool
            </div>
            <div className="flex flex-wrap gap-space-2">
              {pools.data.map((p: RevenuePoolDto) => (
                <FilterChip
                  key={p.id}
                  label={`${p.code} · ${p.name}`}
                  active={selectedPoolIds.includes(p.id)}
                  onClick={() => togglePool(p.id)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="secondary" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      </Card>

      <ClaimsTable
        rows={claims.data?.data ?? []}
        loading={claims.isLoading}
        sortBy={query.sortBy}
        sortDirection={query.sortDirection}
        onSort={(col) => {
          const isCurrent = query.sortBy === col;
          const nextDir = isCurrent && query.sortDirection === "asc" ? "desc" : "asc";
          setParams({ sortBy: col, sortDirection: nextDir });
        }}
      />

      <div className="flex items-center justify-between text-small text-gray-mid">
        <div>
          {claims.data
            ? `${claims.data.totalCount.toLocaleString()} claim${claims.data.totalCount === 1 ? "" : "s"} · page ${currentPage} of ${totalPages}`
            : "—"}
        </div>
        <div className="flex gap-space-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setParam("page", String(currentPage - 1))}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => setParam("page", String(currentPage + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

function parseDecimalParam(value: string | null): number | undefined {
  if (value === null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

// PagedQuery.sortDirection is a strict "asc" | "desc" union, but URL
// params are typed as `string | null`. Narrow with a guard so unknown
// values fall through to undefined instead of being passed downstream.
function parseSortDirection(value: string | null): "asc" | "desc" | undefined {
  return value === "asc" || value === "desc" ? value : undefined;
}

function parseRevisionState(value: string | null): RevisionState | undefined {
  return value === "None" || value === "RevisionInProgress" ? value : undefined;
}

function FilterChip({
  label, active, onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "h-8 px-space-3 font-display text-label font-semibold uppercase tracking-label rounded-md " +
        "border transition-colors duration-fast " +
        (active
          ? "bg-gold text-onyx border-gold"
          : "bg-white-pure text-gray-mid border-border-light hover:border-gold")
      }
    >
      {label}
    </button>
  );
}

function ClaimsTable({
  rows, loading, sortBy, sortDirection, onSort,
}: {
  rows: CommissionClaimSummaryDto[];
  loading: boolean;
  sortBy?: string;
  sortDirection?: string;
  onSort: (column: string) => void;
}) {
  const { tz } = useDateFormatter();
  if (loading) {
    return (
      <Card>
        <div className="font-display text-label uppercase tracking-label text-gray-mid">Loading…</div>
      </Card>
    );
  }
  if (rows.length === 0) {
    return (
      <Card>
        <div className="text-center py-space-4">
          <div className="font-display font-bold text-body text-onyx">No claims match these filters.</div>
          <div className="text-small text-gray-mid mt-space-1">Adjust the filter chips or clear them to widen the search.</div>
        </div>
      </Card>
    );
  }
  return (
    <Card className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-small">
          <thead className="bg-onyx">
            <tr>
              <SortableTh label="Claim #" col="claimNumber" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} />
              <Th label="Pool" />
              <Th label="Claimant" />
              <Th label="Loan" />
              <Th label="Description" />
              <Th label="Gross" align="right" />
              <Th label="%" align="right" />
              <SortableTh label="Amount" col="amount" align="right" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} />
              <SortableTh label="Status" col="status" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} />
              <Th label="Round" align="right" />
              <SortableTh label="Created" col="createdAt" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} />
              <SortableTh label="Last Action" col="modifiedAt" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} />
              <SortableTh label="Total Paid" col="totalPaid" align="right" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} />
              <SortableTh label="Balance" col="balanceRemaining" align="right" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-border-light hover:bg-gold-highlight/10 transition-colors duration-fast">
                <td className="px-space-3 py-space-3 font-mono">
                  <Link to={`/commissions/${c.id}`} className="text-gold-shadow hover:underline">
                    {c.claimNumber}
                  </Link>
                </td>
                <td className="px-space-3 py-space-3">
                  <span className="font-display text-label font-semibold tracking-label text-gray-mid">{c.poolCode}</span>{" "}
                  <span className="text-onyx">{c.poolName}</span>
                </td>
                <td className="px-space-3 py-space-3">
                  <ClaimClaimant
                    status={c.status}
                    claimantUserId={c.claimantUserId}
                    claimantDisplayName={c.claimantDisplayName}
                    heldReason={c.heldReason}
                  />
                </td>
                <td className="px-space-3 py-space-3 text-gray-mid">
                  <ClaimLoan
                    loanId={c.loanId}
                    loanNumber={c.loanNumber}
                    loanReference={c.loanReference}
                  />
                </td>
                <td className="px-space-3 py-space-3 text-gray-mid">{truncate(c.serviceDescription, 50)}</td>
                <td className="px-space-3 py-space-3 text-right tabular-nums">{currency(c.grossRevenue)}</td>
                <td className="px-space-3 py-space-3 text-right tabular-nums">{percent(c.commissionPercent)}</td>
                <td className="px-space-3 py-space-3 text-right tabular-nums font-semibold">{currency(c.commissionAmount)}</td>
                <td className="px-space-3 py-space-3">
                  <div className="flex flex-wrap gap-space-1">
                    <ClaimStatusBadge status={c.status} />
                    <RevisionStateBadge state={c.revisionState} />
                  </div>
                </td>
                <td className="px-space-3 py-space-3 text-right tabular-nums">
                  {c.reviewRound > 0 ? c.reviewRound : "—"}
                </td>
                <td className="px-space-3 py-space-3 text-gray-mid">{shortDate(c.createdAt, tz)}</td>
                <td className="px-space-3 py-space-3 text-gray-mid">{shortDate(c.modifiedAt, tz)}</td>
                <td className="px-space-3 py-space-3 text-right tabular-nums">{currency(c.totalPaid)}</td>
                <td className="px-space-3 py-space-3 text-right tabular-nums">{currency(c.balanceRemaining)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function Th({ label, align = "left" }: { label: string; align?: "left" | "right" }) {
  return (
    <th className={`px-space-3 py-space-3 font-display text-[10px] font-semibold uppercase tracking-label text-gold ${align === "right" ? "text-right" : "text-left"}`}>
      {label}
    </th>
  );
}

function SortableTh({
  label, col, align = "left", sortBy, sortDirection, onSort,
}: {
  label: string;
  col: string;
  align?: "left" | "right";
  sortBy?: string;
  sortDirection?: string;
  onSort: (col: string) => void;
}) {
  const active = sortBy === col;
  const arrow = active ? (sortDirection === "asc" ? " ▲" : " ▼") : "";
  return (
    <th className={`px-space-3 py-space-3 ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className={
          "font-display text-[10px] font-semibold uppercase tracking-label " +
          (active ? "text-gold" : "text-gold/70 hover:text-gold")
        }
      >
        {label}{arrow}
      </button>
    </th>
  );
}
